import React, { useEffect, useState } from 'react';
import { Board } from '../game/Board';
import { ScrollDial } from '../game/ScrollDial';
import { MoveTimer } from '../game/MoveTimer';
import { HintButton } from '../game/HintButton';
import { PauseMenu } from '../game/PauseMenu';
import { ChatPanel } from '../game/ChatPanel';
import { TimeOutModal } from '../game/TimeOutModal';
import { Button } from '../ui/Button';
import { useGameState } from '../../hooks/useGameState';
import { useAppContext } from '../../contexts/AppContext';
import { PLAYERS, cn } from '../../lib/utils';
import { Pause, MessageSquare, RotateCcw } from 'lucide-react';
import { GameSettings } from '../settings/SettingsScreen';
interface GameScreenProps {
  onBack: () => void;
  onFinish: (winner: number | null, stats: {
    moves: number;
    duration: number;
    connectedChips: number;
    averageMoveTime: number;
  }) => void;
  settings: GameSettings & {
    hideProduct?: boolean;
  };
  onSettingsChange: (settings: GameSettings) => void;
}
export function GameScreen({
  onBack,
  onFinish,
  settings,
  onSettingsChange
}: GameScreenProps) {
  const {
    user,
    updateUser,
    updateAnonymousProgress
  } = useAppContext();
  const {
    board,
    currentPlayer,
    dials,
    product,
    status,
    winner,
    lastMoveProduct,
    turnCount,
    hintsRemaining,
    handleDialChange,
    placeChip,
    canPlace,
    resetDials,
    getHint
  } = useGameState();
  const [prevDials, setPrevDials] = useState<{
    a: number;
    b: number;
  } | null>(null);
  const [isPauseOpen, setIsPauseOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isTimeOutModalOpen, setIsTimeOutModalOpen] = useState(false);
  const [emoteMessage, setEmoteMessage] = useState<string | null>(null);
  const [gameStartTime] = useState(Date.now());
  const [moveCount, setMoveCount] = useState(0);
  const [freeTimeExtensionUsed, setFreeTimeExtensionUsed] = useState(false);
  useEffect(() => {
    setPrevDials(dials);
  }, [turnCount]);
  useEffect(() => {
    if (status === 'playing' && lastMoveProduct) {
      setMoveCount(prev => prev + 1);
    }
  }, [lastMoveProduct, status]);
  const isALocked = turnCount > 0 && prevDials && dials.b !== prevDials.b;
  const isBLocked = turnCount > 0 && prevDials && dials.a !== prevDials.a;
  const [timeLeft, setTimeLeft] = useState(settings.timePerRound);
  useEffect(() => {
    if (status !== 'playing' || isPauseOpen || isTimeOutModalOpen) return;
    setTimeLeft(settings.timePerRound);
    const timer = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          setIsTimeOutModalOpen(true);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [currentPlayer, status, isPauseOpen, isTimeOutModalOpen, settings.timePerRound]);
  useEffect(() => {
    if (status === 'won') {
      const duration = Math.floor((Date.now() - gameStartTime) / 1000);
      const connectedChips = Object.keys(board).filter(k => board[parseInt(k)] === winner).length;
      const stats = {
        moves: moveCount,
        duration,
        connectedChips,
        averageMoveTime: duration / moveCount
      };
      // Update user stats or anonymous progress
      const isWin = winner === 1;
      if (user) {
        const newStreak = isWin ? user.currentStreak + 1 : 0;
        updateUser({
          totalGames: user.totalGames + 1,
          wins: user.wins + (isWin ? 1 : 0),
          losses: user.losses + (isWin ? 0 : 1),
          currentStreak: newStreak,
          bestStreak: Math.max(user.bestStreak, newStreak),
          totalPlayTime: user.totalPlayTime + duration,
          xp: user.xp + (isWin ? 100 : 50)
        });
      } else {
        updateAnonymousProgress({
          games: 1,
          wins: isWin ? 1 : 0,
          playTime: duration,
          xp: isWin ? 100 : 50
        });
      }
      setTimeout(() => onFinish(winner, stats), 2000);
    }
  }, [status, winner, onFinish, board, moveCount, gameStartTime, user, updateUser, updateAnonymousProgress]);
  const handleBuyTime = () => {
    setTimeLeft(30);
    setIsTimeOutModalOpen(false);
    if (!freeTimeExtensionUsed) {
      setFreeTimeExtensionUsed(true);
    } else {
      // Deduct 50 diamonds
      console.log('Deducting 50 diamonds');
    }
  };
  const handleAcceptLoss = () => {
    setIsTimeOutModalOpen(false);
    const duration = Math.floor((Date.now() - gameStartTime) / 1000);
    const connectedChips = Object.keys(board).filter(k => board[parseInt(k)] === currentPlayer).length;
    const stats = {
      moves: moveCount,
      duration,
      connectedChips,
      averageMoveTime: duration / (moveCount || 1)
    };
    // Update stats for loss
    if (user) {
      updateUser({
        totalGames: user.totalGames + 1,
        losses: user.losses + 1,
        currentStreak: 0,
        totalPlayTime: user.totalPlayTime + duration,
        xp: user.xp + 25
      });
    } else {
      updateAnonymousProgress({
        games: 1,
        wins: 0,
        playTime: duration,
        xp: 25
      });
    }
    onFinish(currentPlayer === 1 ? 2 : 1, stats);
  };
  const handleSendEmote = (emote: string) => {
    setEmoteMessage(emote);
    setTimeout(() => setEmoteMessage(null), 3000);
  };
  const handleResign = () => {
    setIsPauseOpen(false);
    const duration = Math.floor((Date.now() - gameStartTime) / 1000);
    const connectedChips = Object.keys(board).filter(k => board[parseInt(k)] === 1).length;
    const stats = {
      moves: moveCount,
      duration,
      connectedChips,
      averageMoveTime: duration / (moveCount || 1)
    };
    // Update stats for resignation
    if (user) {
      updateUser({
        totalGames: user.totalGames + 1,
        losses: user.losses + 1,
        currentStreak: 0,
        totalPlayTime: user.totalPlayTime + duration,
        xp: user.xp + 25
      });
    } else {
      updateAnonymousProgress({
        games: 1,
        wins: 0,
        playTime: duration,
        xp: 25
      });
    }
    onFinish(currentPlayer === 1 ? 2 : 1, stats);
  };
  const activePlayer = PLAYERS[currentPlayer];
  const hideProduct = settings.hideProduct || false;
  return <div className="flex flex-col h-full bg-slate-50" data-id="element-323">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between bg-white border-b border-slate-200" data-id="element-324">
        <button onClick={() => setIsPauseOpen(true)} className="p-2 -ml-2 text-slate-400 hover:bg-slate-100 rounded-full" data-id="element-325">
          <Pause size={20} data-id="element-326" />
        </button>
        <div className="flex items-center gap-3" data-id="element-327">
          <div className={cn('w-2 h-2 rounded-full animate-pulse', activePlayer.colorClass)} data-id="element-328" />
          <span className="font-bold text-slate-700" data-id="element-329">
            {activePlayer.name}'s Turn
          </span>
        </div>
        <div className="flex gap-2" data-id="element-330">
          <HintButton onHint={getHint} hintsRemaining={hintsRemaining} onBuyHints={() => console.log('Buy hints')} data-id="element-331" />
          <button onClick={() => setIsChatOpen(true)} className="p-2 text-slate-400 hover:bg-slate-100 rounded-full" data-id="element-332">
            <MessageSquare size={20} data-id="element-333" />
          </button>
        </div>
      </div>

      {/* Emote Message */}
      {emoteMessage && <div className="absolute top-20 left-1/2 -translate-x-1/2 z-30 animate-in slide-in-from-top duration-300" data-id="element-334">
          <div className="bg-white px-6 py-3 rounded-full shadow-lg border border-slate-200 font-bold text-slate-700" data-id="element-335">
            {emoteMessage}
          </div>
        </div>}

      {/* Game Area */}
      <div className="flex-1 flex flex-col p-5 gap-6 overflow-y-auto" data-id="element-336">
        {/* Timer & Info */}
        <div className="flex justify-between items-center px-2" data-id="element-337">
          <MoveTimer seconds={timeLeft} totalSeconds={settings.timePerRound} data-id="element-338" />
          <div className="text-sm font-medium text-slate-500" data-id="element-339">
            Last: {lastMoveProduct ? lastMoveProduct : '-'}
          </div>
        </div>

        {/* Board - Larger with minimal margins */}
        <div className="flex-1 flex items-center justify-center" data-id="element-340">
          <Board boardState={board} lastMoveProduct={lastMoveProduct || undefined} onSquareClick={() => {}} highlightProducts={hideProduct ? [] : [product]} data-id="element-341" />
        </div>

        {/* Controls */}
        <div className="bg-white rounded-3xl p-6 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] space-y-6" data-id="element-342">
          {/* Dials & Product */}
          <div className="flex items-center justify-between gap-4" data-id="element-343">
            <ScrollDial value={dials.a} onChange={v => handleDialChange('a', v)} isLocked={isALocked} label="Dial A" colorClass={activePlayer.id === 1 ? 'bg-violet-100 text-violet-700' : 'bg-teal-100 text-teal-700'} data-id="element-344" />

            <div className="flex flex-col items-center" data-id="element-345">
              <span className="text-xs font-bold text-slate-400 mb-1 uppercase" data-id="element-346">
                Product
              </span>
              <div className="text-4xl font-black text-slate-800 tabular-nums" data-id="element-347">
                {hideProduct ? '?' : product}
              </div>
              {hideProduct && <div className="text-xs text-amber-600 font-semibold mt-1" data-id="element-348">
                  Hidden Mode
                </div>}
            </div>

            <ScrollDial value={dials.b} onChange={v => handleDialChange('b', v)} isLocked={isBLocked} label="Dial B" colorClass={activePlayer.id === 1 ? 'bg-violet-100 text-violet-700' : 'bg-teal-100 text-teal-700'} data-id="element-349" />
          </div>

          {/* Undo Button */}
          {turnCount > 0 && (isALocked || isBLocked) && <button onClick={resetDials} className="w-full py-2 text-sm font-semibold text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center justify-center gap-2" data-id="element-350">
              <RotateCcw size={16} data-id="element-351" />
              Reset Dials (Change Different One)
            </button>}

          {/* Action Button */}
          <Button fullWidth size="xl" onClick={placeChip} disabled={!canPlace} className={cn('transition-all', !canPlace && 'opacity-50 grayscale')} data-id="element-352">
            {canPlace ? 'Place Chip' : 'Square Taken'}
          </Button>
        </div>
      </div>

      {/* Time Out Modal */}
      <TimeOutModal isOpen={isTimeOutModalOpen} onBuyTime={handleBuyTime} onAcceptLoss={handleAcceptLoss} canBuyFree={!freeTimeExtensionUsed} playerName={activePlayer.name} data-id="element-353" />

      {/* Pause Menu */}
      <PauseMenu isOpen={isPauseOpen} onClose={() => setIsPauseOpen(false)} onResign={handleResign} onHome={onBack} settings={settings} onSettingsChange={onSettingsChange} data-id="element-354" />

      {/* Chat Panel */}
      <ChatPanel isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} onSendEmote={handleSendEmote} data-id="element-355" />
    </div>;
}